import { Message, Modal, Radio, Spin } from "@arco-design/web-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type MouseEvent } from "react";

import {
  fetchDispatcherSelectionCandidates,
  fetchDispatcherSelectionOrderDetails,
} from "../repo/customizationRepo";
import type {
  CustomizationDispatcherOrderDetailItem,
  CustomizationDispatcherOrderDetailType,
  CustomizationDispatcherSelectionCandidate,
} from "../types";

import "./DispatcherSelectionModal.css";

const DISPATCHER_AVATAR_SUFFIX = "?imageView2/1/w/100/h/100/q/80";
const DEFAULT_DISPATCHER_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 36'%3E%3Crect width='36' height='36' rx='18' fill='%23f2f3f5'/%3E%3Ccircle cx='18' cy='14' r='7' fill='%23c9cdd4'/%3E%3Cpath d='M6 31c2.5-6 7-9 12-9s9.5 3 12 9' fill='%23c9cdd4'/%3E%3C/svg%3E";

export const DISPATCHER_SELECTION_CANDIDATES_QUERY_KEY = [
  "customization",
  "dispatcher-selection-candidates",
] as const;

type DispatcherSelectionModalProps = {
  visible: boolean;
  selectedUserId?: string;
  selectedDisplayName?: string;
  onClose: () => void;
  onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
};

type ExpandedDetailKey = {
  userId: string;
  detailType: CustomizationDispatcherOrderDetailType;
};

/** 格式化计调头像 URL，兼容旧系统七牛裁剪参数与反斜杠拼接。 */
export function formatDispatcherAvatarUrl(avatarUrl?: string): string {
  const normalized = avatarUrl?.trim().replaceAll("\\", "/");
  if (!normalized) {
    return DEFAULT_DISPATCHER_AVATAR;
  }
  if (normalized.includes("imageView2/")) {
    return normalized;
  }
  return `${normalized}${DISPATCHER_AVATAR_SUFFIX}`;
}

/** 预取第一计调选择候选，减轻首次打开弹窗等待。 */
export function usePrefetchDispatcherSelectionCandidates(enabled = true) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) {
      return;
    }
    void queryClient.prefetchQuery({
      queryKey: DISPATCHER_SELECTION_CANDIDATES_QUERY_KEY,
      queryFn: fetchDispatcherSelectionCandidates,
      staleTime: 60_000,
    });
  }, [enabled, queryClient]);
}

/** 第一计调选择弹窗，对齐旧系统 SelectJiDiao。 */
export function DispatcherSelectionModal({
  visible,
  selectedUserId,
  selectedDisplayName,
  onClose,
  onConfirm,
}: DispatcherSelectionModalProps) {
  const [pendingUserId, setPendingUserId] = useState(selectedUserId ?? "");
  const [clearedSelection, setClearedSelection] = useState(false);
  const [expandedDetail, setExpandedDetail] = useState<ExpandedDetailKey | null>(null);
  const candidatesQuery = useQuery({
    queryKey: DISPATCHER_SELECTION_CANDIDATES_QUERY_KEY,
    queryFn: fetchDispatcherSelectionCandidates,
    enabled: visible,
    staleTime: 60_000,
  });
  const orderDetailsQuery = useQuery({
    queryKey: [
      "customization",
      "dispatcher-selection-order-details",
      expandedDetail?.userId,
      expandedDetail?.detailType,
    ],
    queryFn: () =>
      fetchDispatcherSelectionOrderDetails(
        expandedDetail!.userId,
        expandedDetail!.detailType,
      ),
    enabled: visible && Boolean(expandedDetail),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!visible) {
      return;
    }
    setPendingUserId(selectedUserId ?? "");
    setClearedSelection(false);
    setExpandedDetail(null);
  }, [selectedUserId, visible]);

  const candidates = candidatesQuery.data ?? [];
  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.userId === pendingUserId),
    [candidates, pendingUserId],
  );
  const hasPendingSelection = Boolean(pendingUserId);
  const footerLabel = selectedCandidate
    ? `已选择：${selectedCandidate.displayName}`
    : !clearedSelection && selectedDisplayName?.trim() && selectedUserId && selectedUserId === pendingUserId
      ? `已选择：${selectedDisplayName.trim()}`
      : "未选中";

  const handleRowClick = (userId: string) => {
    if (pendingUserId === userId) {
      setPendingUserId("");
      setClearedSelection(true);
      return;
    }
    setPendingUserId(userId);
    setClearedSelection(false);
  };

  const handleClearSelection = () => {
    setPendingUserId("");
    setClearedSelection(true);
  };

  const handleConfirm = () => {
    if (!pendingUserId) {
      if (clearedSelection || selectedUserId) {
        onConfirm(null);
        return;
      }
      Message.warning("请选择计调人员！");
      return;
    }
    if (!selectedCandidate) {
      Message.warning("请选择计调人员！");
      return;
    }
    onConfirm(selectedCandidate);
  };

  const handleStatClick = (
    event: MouseEvent,
    userId: string,
    detailType: CustomizationDispatcherOrderDetailType,
    count: number,
  ) => {
    event.stopPropagation();
    if (count <= 0) {
      return;
    }
    if (
      expandedDetail?.userId === userId
      && expandedDetail.detailType === detailType
    ) {
      setExpandedDetail(null);
      return;
    }
    setExpandedDetail({ userId, detailType });
  };

  const detailItems: CustomizationDispatcherOrderDetailItem[] = orderDetailsQuery.data ?? [];

  return (
    <Modal
      visible={visible}
      title="选择计调人员"
      footer={null}
      escToExit
      maskClosable
      unmountOnExit
      className="customization-dispatcher-selection-modal"
      style={{ width: 640 }}
      onCancel={onClose}
    >
      <div className="customization-dispatcher-selection-body" aria-label="计调人员列表">
        {candidatesQuery.isLoading ? (
          <div className="customization-dispatcher-selection-loading">
            <Spin tip="正在加载计调人员…" />
          </div>
        ) : null}
        {!candidatesQuery.isLoading && candidates.length === 0 ? (
          <div className="customization-dispatcher-selection-empty">没有计调人员数据！</div>
        ) : null}
        {!candidatesQuery.isLoading
          ? candidates.map((candidate) => {
              const isSelected = candidate.userId === pendingUserId;
              const showPendingDetails =
                expandedDetail?.userId === candidate.userId
                && expandedDetail.detailType === "pending";
              const showFollowingDetails =
                expandedDetail?.userId === candidate.userId
                && expandedDetail.detailType === "following";
              const showDetails = showPendingDetails || showFollowingDetails;
              return (
                <div
                  key={candidate.userId}
                  className={`customization-dispatcher-selection-row${isSelected ? " is-selected" : ""}`}
                  onClick={() => handleRowClick(candidate.userId)}
                >
                  <div className="customization-dispatcher-selection-radio">
                    <Radio checked={isSelected} />
                  </div>
                  <div className="customization-dispatcher-selection-main">
                    <div className="customization-dispatcher-selection-main-row">
                      <div className="customization-dispatcher-selection-profile">
                        <img
                          className="customization-dispatcher-selection-avatar"
                          src={formatDispatcherAvatarUrl(candidate.avatarUrl)}
                          alt={candidate.displayName}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = DEFAULT_DISPATCHER_AVATAR;
                          }}
                        />
                        <span className="customization-dispatcher-selection-name">{candidate.displayName}</span>
                      </div>
                      <div className="customization-dispatcher-selection-stats">
                        <button
                          type="button"
                          className={`is-pending customization-dispatcher-selection-stat${candidate.pendingOrderCount > 0 ? " is-clickable" : ""}`}
                          aria-label={`${candidate.displayName} 未接单 ${candidate.pendingOrderCount}`}
                          disabled={candidate.pendingOrderCount <= 0}
                          onClick={(event) =>
                            handleStatClick(
                              event,
                              candidate.userId,
                              "pending",
                              candidate.pendingOrderCount,
                            )}
                        >
                          未接单:{candidate.pendingOrderCount}
                        </button>
                        <button
                          type="button"
                          className={`is-following customization-dispatcher-selection-stat${candidate.followingOrderCount > 0 ? " is-clickable" : ""}`}
                          aria-label={`${candidate.displayName} 在跟单 ${candidate.followingOrderCount}`}
                          disabled={candidate.followingOrderCount <= 0}
                          onClick={(event) =>
                            handleStatClick(
                              event,
                              candidate.userId,
                              "following",
                              candidate.followingOrderCount,
                            )}
                        >
                          在跟单:{candidate.followingOrderCount}
                        </button>
                        <span className="is-unfinished">未完团:{candidate.unfinishedGroupCount}</span>
                        <span className="is-finished">已完团:{candidate.finishedGroupCount}</span>
                      </div>
                    </div>
                    {showDetails ? (
                      <div
                        className="customization-dispatcher-selection-detail"
                        aria-label={`${candidate.displayName}${showPendingDetails ? "未接单" : "在跟单"}明细`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {orderDetailsQuery.isFetching ? (
                          <div className="customization-dispatcher-selection-detail-loading">
                            <Spin size={16} />
                            <span>正在加载明细…</span>
                          </div>
                        ) : null}
                        {!orderDetailsQuery.isFetching && detailItems.length === 0 ? (
                          <div className="customization-dispatcher-selection-detail-empty">暂无明细</div>
                        ) : null}
                        {!orderDetailsQuery.isFetching
                          ? detailItems.map((item, index) => (
                              <div
                                key={`${item.jidiaoNo}-${index}`}
                                className="customization-dispatcher-selection-detail-item"
                              >
                                <div className="customization-dispatcher-selection-detail-head">
                                  <span className="customization-dispatcher-selection-detail-index">
                                    {index + 1}
                                  </span>
                                  <span className="customization-dispatcher-selection-detail-no">
                                    {item.jidiaoNo}
                                  </span>
                                  {item.salesName ? (
                                    <span className="customization-dispatcher-selection-detail-sales">
                                      {item.salesName}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="customization-dispatcher-selection-detail-name">
                                  {item.businessOpportunityName}
                                </div>
                                <div className="customization-dispatcher-selection-detail-tags">
                                  {item.lastPriceTimeLabel ? (
                                    <span className="customization-dispatcher-selection-detail-tag is-plan">
                                      {item.lastPriceTimeLabel}要方案
                                    </span>
                                  ) : null}
                                  {item.startDateLabel ? (
                                    <span className="customization-dispatcher-selection-detail-tag is-start">
                                      {item.startDateLabel}出发
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            ))
                          : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          : null}
      </div>
      <div className="customization-dispatcher-selection-footer">
        <span className="customization-dispatcher-selection-status">{footerLabel}</span>
        <div className="customization-dispatcher-selection-actions">
          {hasPendingSelection || (!clearedSelection && Boolean(selectedUserId)) ? (
            <button
              type="button"
              className="customization-dispatcher-selection-clear"
              disabled={candidatesQuery.isLoading}
              onClick={handleClearSelection}
            >
              取消选择
            </button>
          ) : null}
          <button
            type="button"
            className="customization-dispatcher-selection-confirm"
            disabled={candidatesQuery.isLoading}
            onClick={handleConfirm}
          >
            确定
          </button>
        </div>
      </div>
    </Modal>
  );
}

type DispatcherSelectionFieldProps = {
  label?: string;
  value?: string;
  displayValue?: string;
  disabled?: boolean;
  onOpen: () => void;
};

/** 第一计调字段触发器，点击后打开专用选人弹窗。 */
export function DispatcherSelectionField({
  label = "第一计调",
  value,
  displayValue,
  disabled = false,
  onOpen,
}: DispatcherSelectionFieldProps) {
  const currentLabel = displayValue?.trim() || value?.trim() || "第一计调";
  const isPlaceholder = !displayValue?.trim() && !value?.trim();

  return (
    <button
      type="button"
      aria-label={label}
      className={`customization-dispatcher-selection-trigger${isPlaceholder ? " is-placeholder" : ""}`}
      disabled={disabled}
      onClick={onOpen}
    >
      <span>{currentLabel}</span>
      <span aria-hidden="true">▼</span>
    </button>
  );
}
